import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  type ReactNode,
} from 'react';

import { getDatabase, getDbPromise, initDatabase } from './database';
import type { DepilawayDatabase, DepilawayCollections } from './database';
import { setupReplication, cleanupReplication, setupPendingTracker } from './sync';
import { useAuthStore } from '../stores/auth.store';
import { useSyncStore } from '../stores/sync.store';
import { AlertTriangle, Trash2, RefreshCw, ChevronDown, ChevronUp, Terminal, ShieldAlert } from 'lucide-react';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { supabase } from '../lib';
import { performBcvSync } from './sync/bcvSync';

// ── Context ────────────────────────────────────────────────────────────

interface DatabaseContextValue {
  db: DepilawayDatabase;
}

const DatabaseContext = createContext<DatabaseContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────

interface DatabaseProviderProps {
  readonly children: ReactNode;
  /** Optional callback invoked when initialisation fails. */
  readonly onError?: (error: Error) => void;
}

export function DatabaseProvider({
  children,
  onError,
}: DatabaseProviderProps): ReactNode {
  const [db, setDb] = useState<DepilawayDatabase | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isAuthLoading = useAuthStore((s) => s.isLoading);
  const targetUserId = user?.id || 'local';

  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const executeReset = async () => {
    setIsResetting(true);
    try {
      if (supabase) {
        await supabase.auth.signOut().catch(() => {});
      }
      // Limpiar almacenamiento local y sesión
      localStorage.clear();
      sessionStorage.clear();

      // Cerrar RxDB para liberar el bloqueo de IndexedDB
      if (db) {
        try {
          await db.remove();
        } catch (e) {
          console.error('Error cerrando RxDB antes de restablecer:', e);
        }
      }

      // Eliminar bases de datos locales IndexedDB
      if (window.indexedDB && window.indexedDB.databases) {
        const dbs = await window.indexedDB.databases();
        for (const dbInfo of dbs) {
          if (dbInfo.name) {
            window.indexedDB.deleteDatabase(dbInfo.name);
          }
        }
      } else {
        window.indexedDB.deleteDatabase('rxdb-dexie-depilaway_local');
        window.indexedDB.deleteDatabase('rxdb-db-depilaway');
        window.indexedDB.deleteDatabase('depilaway');
      }

      globalThis.location.reload();
    } catch (err) {
      console.error('Error resetting database:', err);
      globalThis.location.reload();
    } finally {
      setIsResetting(false);
    }
  };

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  const [contacto, setContacto] = useState<string | null>(null);

  useEffect(() => {
    if (!error || !supabase) return;
    supabase
      .from('app_config')
      .select('value')
      .eq('key', 'support_contact')
      .eq('is_deleted', false)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) {
          setContacto(data.value);
        }
      })
      .catch((err) => {
        console.error('Error fetching support contact:', err);
      });
  }, [error]);

  useEffect(() => {
    if (isAuthLoading) return;

    let active = true;

    initDatabase(targetUserId)
      .then((database) => {
        if (active) {
          setDb(database);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          const normalisedError =
            err instanceof Error ? err : new Error(String(err));
          setError(normalisedError);
          onError?.(normalisedError);
        }
      });

    return () => {
      active = false;
    };
  }, [targetUserId, isAuthLoading, onError]);

  const autoSyncEnabled = useSyncStore((s) => s.autoSyncEnabled);

  useEffect(() => {
    console.log('[DatabaseProvider] Sync effect triggered:', {
      hasDb: !!db,
      isAuthenticated,
      autoSyncEnabled,
    });
    if (!db) return;
    setupPendingTracker(db);

    if (isAuthenticated && autoSyncEnabled) {
      console.log('[DatabaseProvider] Starting background replication setup');
      setupReplication(db);
    } else {
      console.log('[DatabaseProvider] Auto-sync disabled or logged out. Cleaning up background replication');
      cleanupReplication();
    }
  }, [db, isAuthenticated, autoSyncEnabled]);

// ── Background BCV Rate Sync & Retry ───────────────────────────────
  useEffect(() => {
    if (!db || !isAuthenticated) return;

    let timerId: any = null;
    let isSyncing = false;
    let isUnmounted = false;

    const runBcvBgSync = async () => {
      if (isSyncing || isUnmounted) return;
      isSyncing = true;
      let nextDelay = 1800000; // 30 minutos por defecto

      try {
        // Omite la sincronización de fondo mientras el usuario está en el SetupWizard
        const initDoc = await db.app_config.findOne({ selector: { key: 'system_initialized' } }).exec();
        const isInit = initDoc?.value === 'true';
        if (!initDoc || !isInit) {
          return;
        }

        const nowStr = new Date().toISOString().substring(0, 10);
        const latestLocalDoc = await db.exchange_rates
          .findOne({ selector: { is_deleted: false }, sort: [{ until: 'desc' }] })
          .exec();

        // Si la BD local ya tiene una tasa que vence hoy o después, omitimos la llamada de red y esperamos 1 hora
        if (latestLocalDoc && latestLocalDoc.until >= nowStr) {
          console.log(`[BCV Background Sync] Tasas al día localmente (${latestLocalDoc.until}). Próxima verificación en 1h.`);
          nextDelay = 3600000; // 1 hora
          return;
        }

        const res = await performBcvSync(db, 'bcv_bg_sync');
        if (res.status === 'error') {
          nextDelay = 120000; // Reintentar en 2 min si hubo error de conexión
        } else {
          nextDelay = 1800000; // 30 min si estuvo exitoso o al día
        }
      } catch (err) {
        console.warn('[BCV Background Sync] Falló la sincronización de tasas:', err);
        nextDelay = 120000; // Reintentar en 2 min
      } finally {
        isSyncing = false;
        if (!isUnmounted) {
          timerId = setTimeout(runBcvBgSync, nextDelay);
        }
      }
    };

    timerId = setTimeout(runBcvBgSync, 10000);

    return () => {
      isUnmounted = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [db, isAuthenticated]);

  const currentExpectedName = `depilaway_${user ? user.id : 'local'}`;

  const activeDb = db && db.name === currentExpectedName && !db.destroyed ? db : null;

  // Expose to window for Playwright E2E assertions
  useEffect(() => {
    if (activeDb) {
      (window as any).__db = activeDb;
    }
  }, [activeDb]);

  const contextValue = useMemo(() => (activeDb ? { db: activeDb } : null), [activeDb]);

  // ── Error state ────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F3F8FF] p-4 dark:bg-[#244C5A] transition-colors duration-300">
        <div className="w-full max-w-lg rounded-2xl border border-border/80 bg-white p-6 shadow-xl dark:bg-[#1a3a45]/90 backdrop-blur-md transition-all animate-fade-in text-center">
          <div className="flex flex-col items-center">
            <div className="inline-flex items-center justify-center rounded-2xl bg-brand-orange/10 p-3.5 text-brand-orange mb-4 shadow-sm ring-4 ring-brand-orange/5 animate-pulse">
              <AlertTriangle className="h-7 w-7" />
            </div>
            <h2 className="text-xl font-extrabold text-[#202020] dark:text-[#F3F8FF] tracking-tight">
              Inicialización de Base de Datos Fallida
            </h2>
            <p className="mt-2 text-sm text-[#202020]/70 dark:text-[#F3F8FF]/70 max-w-sm">
              Ocurrió un error al cargar la base de datos local. Esto suele deberse a actualizaciones recientes en la estructura de datos.
            </p>
            
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-surface-secondary/50 px-3 py-1.5 text-xs text-[#202020]/80 dark:text-[#F3F8FF]/80 border border-border/40">
              <ShieldAlert className="h-3.5 w-3.5 text-brand-orange" />
              <span>
                Si el problema persiste, contacta a soporte: <strong className="font-bold">{contacto || 'soporte@depilaway.com'}</strong>
              </span>
            </div>
          </div>

          <div className="mt-6 border-t border-border/60 pt-4 text-left">
            {needRefresh && (
              <div className="mb-4 rounded-xl border border-brand-orange/20 bg-brand-orange/5 p-4 text-left animate-slide-up">
                <div className="flex gap-2.5">
                  <span className="text-xl">🚀</span>
                  <div>
                    <p className="text-xs font-bold text-text-primary">Nueva actualización lista</p>
                    <p className="text-[11px] text-text-secondary mt-0.5">
                      Hay una actualización disponible del sistema que podría solucionar este error automáticamente al instalarse.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => updateServiceWorker(true)}
                  className="mt-3 w-full bg-brand-orange hover:bg-brand-orange/95 text-white text-xs font-bold px-3 py-2 rounded-lg shadow-sm transition-all active:scale-[0.98]"
                >
                  Instalar Actualización
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowDetails(!showDetails)}
              className="flex w-full items-center justify-between rounded-lg bg-surface-secondary/40 px-3 py-2 text-xs font-semibold text-text-secondary hover:bg-surface-secondary transition-all"
            >
              <span className="flex items-center gap-1.5 font-mono">
                <Terminal className="h-3.5 w-3.5" />
                Detalles técnicos del error
              </span>
              {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {showDetails && (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-lg bg-brand-deep p-3 font-mono text-[10px] text-brand-sky border border-[#3B82F6]/25 shadow-inner">
                <pre className="whitespace-pre-wrap break-all leading-normal select-text">
                  {error.stack || error.message || JSON.stringify(error, null, 2)}
                </pre>
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => globalThis.location.reload()}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-orange px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-orange/90 active:scale-95 font-sans"
            >
              <RefreshCw className="h-4 w-4" />
              Reintentar
            </button>
            <button
              type="button"
              onClick={() => setIsResetConfirmOpen(true)}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#EF4444] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#DC2626] active:scale-95 font-sans cursor-pointer"
            >
              <Trash2 className="h-4 w-4" />
              Restablecer Datos
            </button>
          </div>

          <ConfirmDialog
            isOpen={isResetConfirmOpen}
            onClose={() => setIsResetConfirmOpen(false)}
            onConfirm={executeReset}
            title="¿Restablecer Base de Datos Local?"
            message="Esto eliminará toda la caché local de IndexedDB y reiniciará la aplicación para recuperar la conectividad."
            confirmText="Sí, Restablecer"
            variant="danger"
            isLoading={isResetting}
          />
        </div>
      </div>
    );
  }

  // ── Loading state ──────────────────────────────────────────────────

  if (!activeDb) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F3F8FF] dark:bg-[#244C5A]">
        <div className="flex flex-col items-center gap-3">
          <svg
            className="h-10 w-10 animate-spin text-[#FF6900]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          <p className="text-sm font-medium text-[#244C5A] dark:text-[#B9D9EB]">
            Iniciando base de datos…
          </p>
        </div>
      </div>
    );
  }

  return (
    <DatabaseContext.Provider value={contextValue}>
      {children}
    </DatabaseContext.Provider>
  );
}

// ── Hooks ──────────────────────────────────────────────────────────────

/**
 * Returns the initialised RxDB database instance.
 * Must be called within a `<DatabaseProvider>`.
 */
export function useDatabase(): DepilawayDatabase {
  const ctx = useContext(DatabaseContext);
  if (!ctx) {
    throw new Error(
      'useDatabase must be used within a <DatabaseProvider>. ' +
        'Wrap your component tree with <DatabaseProvider>.',
    );
  }
  return ctx.db;
}

/**
 * Returns a typed RxDB collection by name.
 * Must be called within a `<DatabaseProvider>`.
 *
 * @example
 * ```tsx
 * const transactions = useCollection('transactions');
 * ```
 */
export function useCollection<K extends keyof DepilawayCollections>(
  name: K,
): DepilawayCollections[K] {
  const db = useDatabase();
  const collection = db[name] as DepilawayCollections[K] | undefined;

  if (!collection) {
    throw new Error(
      `Collection "${name}" not found in the database. ` +
        'Make sure it was registered in initDatabase().',
    );
  }

  return collection;
}
